/* eslint-disable import/prefer-default-export */
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Popper,
  Select,
  TextField,
} from "@mui/material";
import { PopperProps } from "@mui/material/Popper/BasePopper.types";
import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from "react";
import { toHiragana } from "../utils/common";

type IncrementalProps = {
  label: string;
  options: string[];
  value: string | string[];
  onChange: Function;
  optionLength?: number;
  multiple?: boolean;
  fullWidth?: boolean;
  maxWidth?: number;
  isBlankNoOptions?: boolean; // 最初に検索画面が空の場合にオプションを隠す
  disabled?: boolean;
};

/**
 * ポッパーコンポーネント
 * @param props Popperのプロップス
 * @returns JSX.Element
 */
function PopperComponent(props: PopperProps) {
  return <Popper {...props} placement="bottom-start" />;
}

/**
 * インクリメンタルサーチ
 * @param param0 プロップス
 * @returns JSX.Element
 */
export function IncrementalSearchElement({
  label,
  options,
  value,
  onChange,
  optionLength,
  multiple,
  fullWidth,
  maxWidth = 218,
  isBlankNoOptions = true,
  disabled = false,
}: IncrementalProps) {
  const [inputValueBackup, setInputValueBackup] = useState("");
  const [selectValueBackup, setSelectValueBackup] = useState("");
  const [open, setOpen] = useState(false);
  // 文字列で重複があるとエラーが発生するため。
  const uniqueOptions = useMemo(() => Array.from(new Set(options)),[options]);
  const filterOptions =
    (): ((opts: string[], { inputValue }: { inputValue: string }) => string[]) =>
    (opts: string[], { inputValue }: { inputValue: string }) => {
      console.log("Incremental Search filterOptions:", inputValue, opts);
      return opts
        .filter((o) => toHiragana(o).includes(toHiragana(inputValue)))
        .slice(0, optionLength ?? 5);
    }
  useEffect(() => {
    setOpen(
      !(inputValueBackup === "" && selectValueBackup === "") &&
        !(inputValueBackup === selectValueBackup),
    );
  }, [inputValueBackup, selectValueBackup]);
  useEffect(() => {
    if (!multiple) {
      setInputValueBackup((value as string) ?? "");
      setSelectValueBackup((value as string) ?? "");
    }
  }, [value]);

  return (
    <Box m={1} width={fullWidth ? "calc(100% - 16px)" : "calc(50% - 16px)"}>
      {multiple ? (
        <Autocomplete
          freeSolo={false}
          multiple
          fullWidth
          // eslint-disable-next-line react/no-unstable-nested-components
          PopperComponent={PopperComponent}
          options={uniqueOptions}
          // filterOptions={(opts, {inputValue}) =>
          //   opts.filter(o => toHiragana(o).includes(toHiragana(inputValue))).slice(0, optionLength ?? 5)
          // }
          value={value as string[]}
          onChange={(e, val) => onChange(val)}
          // onInputChange={(e, value) => handleStorehouseChange(value)}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              inputProps={{ ...params.inputProps, maxLength: 100 }}
            />
          )}
          limitTags={2}
          disabled={disabled}
        />
      ) : (
        <Autocomplete
          freeSolo={false}
          fullWidth
          // eslint-disable-next-line react/no-unstable-nested-components
          open={isBlankNoOptions ? open : undefined}
          onInputChange={(e, val) => {
            if (isBlankNoOptions) setInputValueBackup(val);
          }}
          popupIcon={null}
          PopperComponent={PopperComponent}
          options={uniqueOptions}
          filterOptions={filterOptions()}
          value={(value as string) || null}
          onChange={(e, val, reason) => {
            console.log("Incremental Search onChange：", e, val, reason);
            setSelectValueBackup(val ?? "");
            onChange(val);
          }}
          onBlur={() => setOpen(false)}
          sx={{ height: "40px", maxWidth }}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              inputProps={{ ...params.inputProps, maxLength: 100 }}
            />
          )}
          disabled={disabled}
        />
      )}
    </Box>
  );
}

type SelectFormProps = {
  id: string;
  label: string;
  optionList: string[] | readonly string[];
  value: string;
  setValue: Dispatch<SetStateAction<string>> | Function;
  required?: boolean;
  disabled?: boolean;
  isContainBlank?: boolean;
  fullWidth?: boolean;
  minWidth?: number;
  maxWidth?: number | string;
  renderValue?: ((value: string) => ReactNode);
  dummyValues?: string[]; // 候補に表示はされず選択もできないが、初期表示はされるようにする
  allowValueNotInOptions?: boolean; // optionListに含まれていない値がvalueに入っている場合にそれを表示する。
};

/**
 * 選択フォーム
 * @param root0 選択フォームのプロップス
 * @returns JSX.Element
 */
export function SelectForm({
  id,
  label,
  optionList,
  value,
  setValue,
  required,
  disabled,
  isContainBlank,
  fullWidth,
  minWidth = 160,
  maxWidth = 'none',
  renderValue,
  dummyValues,
  allowValueNotInOptions,
}: SelectFormProps) {
  // optionsの中に値が含まれているかチェック
  const isInOptions = useMemo(() => optionList.some(o => o === value), [value]);
  return (
    <Box width={fullWidth ? "100%" : "50%"} m={0} minWidth={minWidth}>
      <Box p={1}>
        <FormControl sx={{ minWidth, width: "100%", maxWidth }} required={required}>
          <InputLabel
            id={`${id}-label`}
            sx={{
              top: "-6px",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -4px) scale(0.75)",
              },
              "& .MuiFormLabel-asterisk": {
                color: "var(--red-800)",
              },
            }}
          >
            {label}
          </InputLabel>
          <Select
            labelId={`${id}-label`}
            label={label}
            id={id}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ height: "40px" }}
            disabled={disabled}
            renderValue={renderValue}
          >
            {isContainBlank && (
              // eslint-disable-next-line no-irregular-whitespace, react/self-closing-comp
              <MenuItem key="" value="">
                &emsp;
              </MenuItem>
            )}
            {dummyValues && // 初期値としては表示するが選択肢には表示しない値
              dummyValues.map((dVal) => (
                <MenuItem key={dVal} value={dVal} disabled sx={{display: 'none'}}>{dVal}</MenuItem>
              ))
            }
            {optionList.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
            {allowValueNotInOptions && !isInOptions &&
              <MenuItem key={value} value={value} disabled sx={{display: 'none'}}>{value}</MenuItem>
            }
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
